// TTV-1 — **THE TRUSTED TV ATTRIBUTION TAG'S LAW** (her order, 2026-08-10, recovered after the
// machine rebooted mid-build). Shared and importing NOTHING, so `scripts/ttv-tag.mjs` can load and
// RUN every law in here on the FAST tier with no browser, no database and no network — the entry
// requirement every law in `src/*.js` is held to.
//
// **WHAT SHIPPED AND WHAT DID NOT, DECLARED HERE RATHER THAN LEFT AS AN ABSENCE (R17/R160: no silent
// caps).** The vendor supplied ONE blob carrying two unrelated things: an ATTRIBUTION tag (the
// Amazon `aat` pixel, its PageView, and the Beeswax segment pixel) and a MEMBER-FACING SURVEY (a
// full-viewport modal, `z-index: 2147483647`, opening seven seconds after load on every page and
// posting the answer and `location.href` to `p3.trustedtv.com`). **Her ruling: the attribution half
// ships, the survey is PARKED for her explicit word** — a modal over an open room or the inbox is a
// member-visible surface, and this house's standing law is that those need her word IN ADVANCE. The
// parked half is named in `TTV_PARKED` so a session reading this file cannot mistake its absence for
// an oversight, and so adding it is a decision somebody makes on purpose.
//
// **AND THE VENDOR'S BLOB IS BROKEN VERBATIM IN A HEAD, WHICH IS WHY THE HEAD SNIPPET IS NOT A
// BYTE COPY (measured, not assumed).** Its segment-pixel block calls `document.body.appendChild`
// with NO try/catch, and `document.body` is `null` while the head is parsing — that THROWS, and a
// throw aborts the remainder of the script block it is in. Dropped in verbatim, the Amazon pixel
// fires and the Beeswax segment pixel never does, silently, with the tag looking perfectly
// installed. The append is deferred to `DOMContentLoaded` when there is no body yet; nothing else
// about the vendor's behaviour is changed, and the fixture holds the deferral BY NAME.

// ---------------------------------------------------------------------------
// HER VENDOR'S OWN IDENTIFIERS, DECLARED ONCE.
//
// `index.html` is STATIC and cannot import (N20-4's own note, one tag up in the same head), so these
// cannot be shared with the snippet by construction — the GATE holds the two against each other and
// goes RED if they drift. That is the same arrangement `siteMeta.js` and the `<title>` already have,
// and it is the reason these are exported rather than inlined: a fixture can only compare two things
// it can both read.
export const TTV_ENDPOINT_ID = "40c663f9-d09d-463c-abe1-38dae83d6b38";
export const TTV_ENDPOINT_HOST = "https://p3.trustedtv.com";
export const TTV_AMAZON_TAG = "ff1bd46c-c5a5-4bca-84c9-a747ea97e7b6";
export const TTV_AMAZON_REGION = "NA";
export const TTV_BUZZ_KEY = "trustedtv";
export const TTV_SEGMENT_KEY = "trustedtv-1069";

// ---------------------------------------------------------------------------
// WHAT THIS HOUSE PARKED, AND WHY. Read by the fixture, which holds BOTH directions: the parked
// surface must be absent from the shipped head, and this roster must name it. A roster that went
// empty while the surface stayed out would read as "nothing was ever parked".
export const TTV_PARKED = [
  {
    what: "the 'How did you hear about us?' survey modal",
    why: "a full-viewport member-facing surface on every page; her standing law is that a "
       + "member-visible surface needs her explicit word in advance",
    markers: ["ttv-survey-host", "zp-overlay", "attachShadow", "SurveyShown"],
  },
];

// The one event the attribution half sends. Named so a call site cannot introduce a second by typing
// a string: an unrostered event is REFUSED rather than sent (R17/R25 — an unrostered key falling
// through to a plausible wrong answer is how a signal goes quietly wrong).
export const TTV_EVENTS = ["PageView"];
export const isTtvEvent = (name) => TTV_EVENTS.includes(String(name || ""));

// ---------------------------------------------------------------------------
// THE PRIVACY CLAUSE, SATISFIED BY CONSTRUCTION RATHER THAN BY FILTERING.
//
// VIBE-1's clause 3 — no member personal data on the measurement rail — binds here too, and this
// tag meets it the strongest way available: **a PageView carries no payload at all.** There is no
// third argument, so there is no key that could ever hold a member fact, and the refusal below is
// not a filter over a roster but a flat refusal of any payload whatsoever.
//
// That is deliberately STRICTER than `sendPixelEvent`, which must carry `price_usd` for a purchase.
// This tag has no purchase path — Roku and Vibe carry the money signal — so the safest shape is
// available and is taken. If a purchase event is ever added here, this refusal is the thing that
// must be widened ON PURPOSE, in the open, rather than a roster somebody quietly appends to.
export function ttvPayloadRefusal(payload) {
  if (payload === null || payload === undefined) return "";
  return "the TrustedTV tag sends no payload on any event (a PageView carries no member fact by "
       + "construction); adding one is a deliberate widening, not a call-site decision";
}

// ---------------------------------------------------------------------------
// THE ONE DOOR. Every TrustedTV call on this site goes through here, so a call site cannot reach
// `window.amzn` itself and walk around the rules above — and the fixture holds that there is no
// second caller.
//
// `win` is a PARAMETER rather than a reach for the global, so a gate RUNS this against a fake window
// and reads back exactly what was sent, with no browser (locked-door.ts's own repair: pure side
// effects on `window` meant a live Chrome was the only instrument that could reach it).
//
// **A MISSING TAG IS NOT AN ERROR AND MUST NOT BE ONE.** The vendor's loader is `async`, an ad
// blocker may remove it outright, and neither is a fault of this site — so an absent `amzn` answers
// a named reason and never throws. The vendor's own stub queues calls made before its script lands,
// which is why this fires the moment the route changes rather than waiting for anything.
export function sendTtvEvent(win, name, payload = null) {
  if (!win || typeof win.amzn !== "function") return { sent: false, reason: "tag_absent" };
  if (!isTtvEvent(name)) return { sent: false, reason: "unrostered_event" };
  const refusal = ttvPayloadRefusal(payload);
  if (refusal) return { sent: false, reason: "payload_refused", detail: refusal };
  win.amzn("trackEvent", name);
  return { sent: true, name };
}
